import { ContentfulImage } from '@components/contentful-image'
import { Markdown } from '@components/markdown'
import { ActionButton } from '@contentful-components/action-button'
import type { BentoPieceRectangleFragment } from '@graphql/queries/GetBentoPieceRectangle.graphql'
import { optimizedBgUrl } from '@lib/utils/contentful-image'
import { isGradientColor } from '@lib/utils/isGradientColor'
import variables, { type Variable } from '@styles/variables'
import classNames from 'classnames'
import Image from 'next/image'
import type { CSSProperties, FunctionComponent } from 'react'

import styles from './bento-rectangle.module.scss'

export const BentoRectangle: FunctionComponent<Props> = ({
    vertical = false,
    headingMd,
    headingColor = 'white',
    copy,
    copyColor = 'white',
    showFdicVisaImage = false,
    showWinnersBadge = false,
    button,
    backgroundType = 'color',
    backgroundColor = 'darkGray',
    image = null,
    imageAlignment = 'flex-end',
    backgroundImage = null,
    backgroundImageMobile = null,
    headingImage = null,
    headingImageAlignment = 'left',
    iconTextList,
    invertLayout = false,
}) => {
    function getBackgroundStyle(): CSSProperties | undefined {
        if (backgroundType === 'video') return
        if (backgroundType === 'image') {
            const bgDesktop = optimizedBgUrl(backgroundImage?.url, 1200)
            const bgMobile = backgroundImageMobile?.url
                ? optimizedBgUrl(backgroundImageMobile.url, 768)
                : bgDesktop

            const base = {
                '--bento-bg-desktop': bgDesktop,
                '--bento-bg-mobile': bgMobile,
            } as CSSProperties

            if (!headingMd && !copy) {
                return { ...base, minHeight: '455px' }
            }
            return base
        }

        if (isGradientColor(backgroundColor)) {
            return { background: variables[backgroundColor as Variable] }
        }

        return { backgroundColor: variables[backgroundColor as Variable] }
    }

    return (
        <div className={styles.boxOne} style={getBackgroundStyle()}>
            <div
                className={classNames({
                    [styles.boxOneContent]: true,
                    [styles.vertical]: vertical,
                    [styles.invertLayout]: invertLayout,
                })}
            >
                {showWinnersBadge && (
                    <div className={styles.winnersBadge}>
                        <Image
                            src='/images/winners-badge.webp'
                            width={200}
                            height={200}
                            alt='Winners Badge'
                            sizes='200px'
                        />
                    </div>
                )}

                <div
                    className={`${styles.boxOneCopy} ${
                        vertical ? styles.vertical : ''
                    }`}
                >
                    <div
                        className={`${styles.heading} 
                        ${
                            headingColor === 'gradient'
                                ? styles.headinggradient
                                : ''
                        }`}
                    >
                        <Markdown
                            tag='p'
                            typeStyle={{
                                _: 'heading-xs',
                                md: 'heading-md',
                                lg: 'heading-lg',
                            }}
                            markdown={headingMd}
                            color={headingColor}
                        />
                    </div>

                    {headingImage && (
                        <div
                            className={classNames(
                                styles.headingImage,
                                styles[headingImageAlignment],
                            )}
                        >
                            <ContentfulImage
                                className={styles.image}
                                url={headingImage.url}
                                width={headingImage.width}
                                height={headingImage.height}
                                alt={headingImage.alt}
                                contentType={headingImage.contentType}
                                sizes='(max-width: 767px) calc(100vw - 40px), (max-width: 1023px) 220px, 440px'
                                priority
                            />
                        </div>
                    )}

                    {copy && (
                        <div
                            className={`${styles.subtitle} ${
                                vertical ? styles.vertical : ''
                            }`}
                        >
                            <Markdown
                                typeStyle={{ _: 'body-lg', md: 'body-md' }}
                                markdown={copy}
                                color={copyColor}
                            />
                        </div>
                    )}

                    {iconTextList && (
                        <div className={styles.iconTextListContainer}>
                            {iconTextList?.items.map((item, i) => (
                                <div
                                    key={i}
                                    className={`${styles.iconTextList}`}
                                >
                                    <ContentfulImage
                                        priority
                                        className={styles.icon}
                                        url={item?.icon?.url}
                                        width={item?.icon?.width}
                                        height={item?.icon?.height}
                                        contentType={item?.icon?.contentType}
                                        sizes='64px'
                                        alt={item?.icon?.alt}
                                    />
                                    <Markdown
                                        tag='span'
                                        typeStyle={{
                                            _: 'body-lg',
                                            md: 'body-md',
                                            lg: 'body-sm',
                                        }}
                                        markdown={item?.text}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {button && (
                        <div className={styles.buttons}>
                            {!!button && (
                                <ActionButton
                                    preset={button?.preset}
                                    {...button}
                                />
                            )}
                        </div>
                    )}

                    {showFdicVisaImage && (
                        <Image
                            className={styles.media}
                            src='/images/visa.svg'
                            width={167}
                            height={52}
                            alt='VISA Insured'
                            sizes='167px'
                        />
                    )}
                </div>

                <div
                    className={`${styles.media} 
                    ${vertical ? styles.vertical : ''}
                    `}
                    style={{
                        alignItems: `${imageAlignment}`,
                        padding: `${
                            imageAlignment === 'center' ? '20px' : '0px'
                        }`,
                    }}
                >
                    <div
                        className={`${styles.imageContainer} ${
                            vertical ? styles.vertical : ''
                        } `}
                    >
                        {image && (
                            <ContentfulImage
                                className={styles.image}
                                url={image.url}
                                width={image.width}
                                height={image.height}
                                alt={image.alt}
                                contentType={image.contentType}
                                sizes='(max-width: 767px) calc(100vw - 40px), (max-width: 1023px) 220px, 440px'
                                style={{
                                    marginBottom: 0,
                                    aspectRatio:
                                        image.width && image.height
                                            ? `${image.width} / ${image.height}`
                                            : undefined,
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BentoRectangle

type Props = Omit<BentoPieceRectangleFragment, 'sys' | 'type'> & {
    index?: number
    vertical?: boolean
}
